import React from 'react';
import styled, { StyledComponentClass } from 'styled-components';
import { colors } from '../../styles';
import cx from 'classnames';
import RangeMarker from './range-marker';

export interface RangeSliderProps {
  className?: string;
  rangeEndpoints: RangeSliderValues;
  rangeValues?: RangeSliderValues;
  rangeInterval?: number;
  onRangeIntervalDisplay?: (interval: number) => string;
  onChange?: (values: object) => void;
}

export interface RangeSliderValues {
  start: number;
  end: number;
}

export interface RangeSliderState {
  moveSlider: boolean;
  movingTarget: any;
  selectedSliderWidth: number;
  selectedSliderLeft: number;
  rangeEndpoints: RangeSliderValues;
  rangeInterval?: number;
  zoomLevel: number;
}

class RangeSlider extends React.Component<RangeSliderProps, RangeSliderState> {
  private baseSliderRef = React.createRef<HTMLDivElement>();
  private previousPageX: number;
  private rangeLeft: number;
  private rangeWidth: number;
  private draggerBuffer: number = 20;

  constructor(props: RangeSliderProps) {
    super(props);
    this.state = {
      moveSlider: false,
      movingTarget: null,
      selectedSliderWidth: 0,
      selectedSliderLeft: 0,
      rangeEndpoints: props.rangeEndpoints,
      rangeInterval: props.rangeInterval,
      zoomLevel: 0,
    };
  }

  componentDidMount() {
    this.rangeLeft = this.baseSliderRef.current.getBoundingClientRect().left;
    this.rangeWidth = this.baseSliderRef.current.getBoundingClientRect().width;

    this.setInitialValues();

    document.addEventListener('mousemove', e => {
      this.mouseMove(e);
    });
    document.addEventListener('mouseup', e => {
      this.mouseUp(e);
    });
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', e => {
      this.mouseMove(e);
    });
    document.removeEventListener('mouseup', e => {
      this.mouseUp(e);
    });
  }

  zoomIn = () => {
    const { zoomLevel } = this.state;
    const { rangeValues } = this.props;
    const { rangeEndpoints } = this.state;

    const spaceOnStart = rangeValues.start - rangeEndpoints.start;
    const spaceOnEnd = rangeEndpoints.end - rangeValues.end;

    const getSpaceLeftPercentage = num =>
      num / (rangeEndpoints.end - rangeEndpoints.start) * 100;

    if (
      getSpaceLeftPercentage(spaceOnStart) <= 5 &&
      getSpaceLeftPercentage(spaceOnEnd) <= 5
    ) {
      return null;
    }

    const newRangeStartPoint =
      spaceOnStart >= 2
        ? rangeEndpoints.start + (spaceOnStart - spaceOnStart % 2) / 2
        : rangeEndpoints.start;
    const newRangeEndPoint =
      spaceOnEnd >= 2
        ? rangeEndpoints.end - (spaceOnEnd - spaceOnEnd % 2) / 2
        : rangeEndpoints.end;
    this.setState(
      {
        rangeEndpoints: {
          start: newRangeStartPoint,
          end: newRangeEndPoint,
        },
        zoomLevel: zoomLevel + 1,
      },
      () => this.setInitialValues(),
    );
  };

  zoomOut = () => {
    const { zoomLevel } = this.state;
    const { rangeValues, rangeEndpoints } = this.props;
    if (zoomLevel === 1) {
      this.setState(
        {
          rangeEndpoints,
          zoomLevel: 0,
        },
        () => this.setInitialValues(),
      );
    } else {
      let rangePointStart = rangeEndpoints.start;
      let rangePointEnd = rangeEndpoints.end;
      for (let i = 1; i <= zoomLevel - 1; i++) {
        const spaceOnStart = rangeValues.start - rangePointStart;
        const spaceOnEnd = rangePointEnd - rangeValues.end;
        rangePointStart =
          spaceOnStart >= 2
            ? rangePointStart + (spaceOnStart - spaceOnStart % 2) / 2
            : rangePointStart;
        rangePointEnd =
          spaceOnEnd >= 2
            ? rangePointEnd - (spaceOnEnd - spaceOnEnd % 2) / 2
            : rangePointEnd;
      }
      this.setState(
        {
          rangeEndpoints: {
            start: rangePointStart,
            end: rangePointEnd,
          },
          zoomLevel: zoomLevel - 1,
        },
        () => this.setInitialValues(),
      );
    }
  };

  updateRangeChange() {
    const { onChange } = this.props;
    const {
      rangeEndpoints,
      selectedSliderLeft,
      selectedSliderWidth,
    } = this.state;
    const rangeWidth = this.baseSliderRef.current.getBoundingClientRect().width;
    const rangePoints = rangeEndpoints.end - rangeEndpoints.start;
    const calculateRangeValue = pointerValue =>
      rangeEndpoints.start + pointerValue / rangeWidth * rangePoints;
    const selectedStartValue = calculateRangeValue(selectedSliderLeft);
    const selectedEndValue = calculateRangeValue(
      selectedSliderLeft + selectedSliderWidth,
    );
    onChange &&
      onChange({
        selectedStartValue,
        selectedEndValue,
      });
  }

  setInitialValues() {
    const { rangeValues } = this.props;
    const { rangeEndpoints } = this.state;
    if (rangeValues && rangeEndpoints) {
      const rangeWidth = this.baseSliderRef.current.getBoundingClientRect()
        .width;
      const rangePoints =
        rangeWidth / (rangeEndpoints.end - rangeEndpoints.start);
      const sliderStart =
        rangePoints * (rangeValues.start - rangeEndpoints.start);
      const sliderEnd = rangePoints * (rangeValues.end - rangeEndpoints.start);
      this.setState(
        {
          selectedSliderLeft: sliderStart,
          selectedSliderWidth: sliderEnd - sliderStart,
        },
        () => {
          this.updateRangeChange();
        },
      );
    }
  }

  setUpRangeMarkers() {
    const markers = [];
    if (this.baseSliderRef && this.baseSliderRef.current) {
      const { rangeEndpoints, rangeInterval } = this.state;
      const { onRangeIntervalDisplay } = this.props;
      const rangeWidth = this.baseSliderRef.current.getBoundingClientRect()
        .width;
      const markersOnSlide =
        (rangeEndpoints.end - rangeEndpoints.start) / rangeInterval;
      const widthPerPoint = rangeWidth / markersOnSlide;
      for (
        let i = rangeEndpoints.start, j = 0;
        i <= rangeEndpoints.end;
        i = i + rangeInterval
      ) {
        markers.push(
          <RangeMarker
            key={i}
            color={colors.grey400}
            left={widthPerPoint * j++}
            label={
              onRangeIntervalDisplay
                ? onRangeIntervalDisplay(i)
                : Math.round(i * 100) / 100
            }
          />,
        );
      }
    }
    return markers;
  }

  getMovingTarget = e => {
    let clickTarget = 'selectedslider';
    const clickPosition = e.pageX - this.rangeLeft;
    const sliderLeft = +e.target.style.left.replace('px', '');
    const sliderWidth = +e.target.style.width.replace('px', '');
    if (
      clickPosition > sliderLeft &&
      clickPosition < sliderLeft + this.draggerBuffer
    ) {
      clickTarget = 'pointerFrom';
    } else if (
      clickPosition < sliderWidth + sliderLeft &&
      clickPosition > sliderWidth + sliderLeft - this.draggerBuffer
    ) {
      clickTarget = 'pointerTo';
    }
    return clickTarget;
  };

  mouseDown = e => {
    this.previousPageX = e.pageX;
    this.setState({
      moveSlider: true,
      movingTarget: this.getMovingTarget(e),
    });
  };
  mouseUp = e => {
    this.previousPageX = null;
    this.setState({
      moveSlider: false,
      movingTarget: null,
    });
  };
  mouseMove = e => {
    const { moveSlider } = this.state;
    moveSlider && this.slide(e);
  };
  slide = e => {
    const { movingTarget } = this.state;
    const selectedSliderElement = document.getElementById('selectedslider')
      .style;
    const pointerFromLeft = +selectedSliderElement.left.replace('px', '');
    const sliderCurrentWidth = +selectedSliderElement.width.replace('px', '');
    const moveDiff = e.pageX - this.previousPageX;
    if (movingTarget === 'selectedslider') {
      let fromLeftPosition = pointerFromLeft + moveDiff;
      if (fromLeftPosition < 0) {
        fromLeftPosition = 0;
      }
      if (fromLeftPosition + sliderCurrentWidth > this.rangeWidth) {
        fromLeftPosition = this.rangeWidth - sliderCurrentWidth;
      }
      this.setState(
        {
          selectedSliderWidth: sliderCurrentWidth,
          selectedSliderLeft: fromLeftPosition,
        },
        () => {
          this.updateRangeChange();
        },
      );
    } else if (movingTarget === 'pointerFrom') {
      let moveLeftPosition = pointerFromLeft + moveDiff;
      let sliderNewWidth = sliderCurrentWidth + moveDiff * -1;
      if (moveLeftPosition < 0) {
        sliderNewWidth = sliderNewWidth + moveLeftPosition;
        moveLeftPosition = 0;
      }
      if (sliderNewWidth <= this.draggerBuffer * 2) {
        moveLeftPosition =
          pointerFromLeft - (this.draggerBuffer * 2 - sliderCurrentWidth);
        sliderNewWidth = this.draggerBuffer * 2;
      }
      this.setState(
        {
          selectedSliderLeft: moveLeftPosition,
          selectedSliderWidth: sliderNewWidth,
        },
        () => {
          this.updateRangeChange();
        },
      );
    } else if (movingTarget === 'pointerTo') {
      let sliderNewWidth = sliderCurrentWidth + moveDiff;
      if (pointerFromLeft + sliderNewWidth > this.rangeWidth) {
        sliderNewWidth = this.rangeWidth - pointerFromLeft;
      }
      if (sliderNewWidth < this.draggerBuffer * 2) {
        sliderNewWidth = this.draggerBuffer * 2;
      }
      this.setState({ selectedSliderWidth: sliderNewWidth }, () => {
        this.updateRangeChange();
      });
    }
    this.previousPageX = e.pageX;
  };

  render() {
    const { className } = this.props;
    const {
      selectedSliderWidth,
      selectedSliderLeft,
      rangeInterval,
      zoomLevel,
    } = this.state;
    return (
      <div className={className}>
        <div className="range-slider-container">
          <div className="zoom-controls">
            <div
              className={cx('zoom-in', { disabled: zoomLevel === 3 })}
              onClick={this.zoomIn}
            >
              +
            </div>
            <div
              className={cx('zoom-out', { disabled: zoomLevel === 0 })}
              onClick={this.zoomOut}
            >
              -
            </div>
          </div>
          <div className="range-slider-components">
            {rangeInterval > 0 && (
              <div draggable={false} className="range-points-container">
                {this.setUpRangeMarkers()}
              </div>
            )}
            <div
              className="slider base-slider"
              ref={this.baseSliderRef}
              onMouseMove={this.mouseMove}
            />
            <div
              id="selectedslider"
              draggable={false}
              {...{
                style: {
                  left: selectedSliderLeft + 'px',
                  width: selectedSliderWidth + 'px',
                },
              }}
              className="slider selected-slider"
              onMouseDown={this.mouseDown}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default styled(RangeSlider)`
  width: 100%;
  position: relative;
  user-drag: none;
  user-select: none;

  .range-slider-container {
    height: 5.25rem;
    position: relative;
    border: 1px solid ${colors.grey300};
  }
  .range-slider-components {
    position: absolute;
    left: 1.25rem;
    right: 4rem;
    .range-marker {
      position: absolute;
      height: 5.15rem;
    }
  }

  .zoom-controls {
    position: absolute;
    display: flex;
    flex-flow: row;
    right: 0;
    top: 0;
    margin-top: 3px;
    margin-right: 3px;
    .zoom-in,
    .zoom-out {
      width: 1.7rem;
      text-align: center;
      border-radius: 2px;
      border: 1px solid ${colors.grey300};
      margin-left: 3px;
      cursor: pointer;
      color: ${colors.grey700};

      &.disabled {
        pointer-events: none;
        color: ${colors.grey150};
        background: ${colors.grey50};
        border: 1px solid ${colors.grey150};
      }

      &:hover {
        border: 1px solid ${colors.grey400};
        background: ${colors.grey100};
      }

      &:active {
        transform: translateY(1px);
      }
    }
  }

  .range-points-container {
    position: absolute;
    display: flex;
    flex-flow: row;
    width: 100%;
    height: 5.25rem;
    user-drag: none;
    user-select: none;
  }

  .slider {
    top: 1.8rem;
    height: 1.25rem;
    position: absolute;
    border-radius: 1rem;
  }
  .base-slider {
    background-color: ${colors.offWhite};
    border: 1px solid ${colors.grey150};
    left: 0;
    width: 100%;
  }

  .selected-slider {
    background-color: ${colors.blue};
    cursor: pointer;
    transition: 0.05s;
    user-drag: none;
    user-select: none;
  }
  .selected-slider::before,
  .selected-slider::after {
    content: '';
    position: absolute;
    width: 1px;
    height: 10px;
    background-color: ${colors.grey150};
    outline: 1px outset ${colors.grey150};
    top: 5px;
  }
  .selected-slider::before {
    left: 8px;
  }
  .selected-slider::after {
    right: 8px;
  }
`;
